import type { UseQueryOptions } from "@tanstack/react-query";
import { mutationOptions, queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import type { AnswerRequest, Form } from "../dynamicForm";
import type { CustomHistoryEntry, HistoryEntry } from "../dynamicHistory/types";
import { api } from "./apiClient";
import type {
  DpiasGridView,
  DpiaById,
  TenantHierarchy,
  DpiaStatus,
  DpiasResponse,
  DpiaShareWithType,
  DpiaRiskAssessmentMatrixResponse,
  DpiaRiskAssessmentPayload,
  DpiaRiskAssessmentsResponse,
} from "./dpia.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { z } from "zod";
import { queryClient } from "./queryClient";
const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/dpia`;
const TENANT_BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/tenant`;
const BASE_ENDPOINT_RISK_ASSESSMENT = `${FORMS_API_BASE_ENDPOINT}/dpia/riskassessment`;

const dpiaKeys = {
  all: () => ["dpia"] as const,
  gridView: () => [...dpiaKeys.all(), "gridview"] as const,
  dpias: () => [...dpiaKeys.all(), "dpias"] as const,
  myTenantsHierarchyWithCreatePermission: () => [...dpiaKeys.all(), "tenantHierarchy"],
  form: () => [...dpiaKeys.all(), "form"] as const,
  preAssessmentForm: () => [...dpiaKeys.all(), "preAssessmentForm"] as const,
  byId: (id: string) => [...dpiaKeys.all(), id] as const,
  history: (id: string) => [...dpiaKeys.byId(id), "history"] as const,
  riskAssessment: (dpiaId: string) => [...dpiaKeys.byId(dpiaId), "riskAssessment"] as const,
  matrix: (dpiaId: string) => [...dpiaKeys.byId(dpiaId), "matrix"] as const,
};

/** Get Dpias */
export const useGetDpias = (options: Pick<UseQueryOptions, "enabled"> = { enabled: true }) => {
  return useQuery({
    queryKey: dpiaKeys.dpias(),
    queryFn: () => api.get(`${BASE_ENDPOINT}`) as Promise<DpiasResponse>,
    throwOnError: true,
    enabled: Boolean(options.enabled),
  });
};

/** Get Dpia grid view */
export const dpiaGridViewQueryOptions = () => {
  return queryOptions({
    queryKey: dpiaKeys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/gridview`) as Promise<DpiasGridView>,
    throwOnError: true,
  });
};

/** Create Dpia */

export const EXTERNAL_DPIA_STATUSES = ["draft", "approved", "rejected"] as const satisfies ReadonlyArray<DpiaStatus>;
export type ExternalDpiaStatus = (typeof EXTERNAL_DPIA_STATUSES)[number];

type InternalCreateDpiaPayload = {
  name: string;
  tenantId: string;
  /** Copy answers from this registration to the new dpia */
  registrationId?: string;
};

type ExternalCreateDpiaPayload = {
  name: string;
  tenantId: string;
  type: "external";
  url?: string;
  summary?: string;
  status: ExternalDpiaStatus;
};

export type CreateDpiaPayload = InternalCreateDpiaPayload | ExternalCreateDpiaPayload;

export const createDpiaMutationOptions = () => {
  return mutationOptions({
    mutationFn: (payload: CreateDpiaPayload) =>
      api.post(
        `${BASE_ENDPOINT}${"registrationId" in payload && payload.registrationId ? `/from-registration` : ""}`,
        payload,
        {
          schema: z.object({ id: z.string(), name: z.string() }),
        }
      ),
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get dpia by id */

export const dpiaByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: dpiaKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}`) as Promise<DpiaById>,
    throwOnError: true,
  });

/** Get dpia history */

type GetDpiaHistoryResponse = {
  history: Array<
    HistoryEntry<
      | CustomHistoryEntry<"dpiaCreated", string>
      | CustomHistoryEntry<"dpiaStatus", DpiaStatus>
      | CustomHistoryEntry<"dpiaName", string>
      | CustomHistoryEntry<"dpiaResponsible", { id: string; firstName: string; lastName: string; email: string }>
      | CustomHistoryEntry<"dpiaParticipants", string | undefined>
      | CustomHistoryEntry<"dpiaSummary", string | undefined>
      | CustomHistoryEntry<"dpiaUrl", string | undefined>
      | CustomHistoryEntry<"dpiaConnectedTenant", { value: string; name: string }>
    >
  >;
};

const getDpiaHistoryResponseSchema = z.object({
  history: z.array(z.custom<GetDpiaHistoryResponse["history"][number]>()),
});

export const dpiaHistoryQueryOptions = (id: string) =>
  queryOptions({
    queryKey: dpiaKeys.history(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}/history`, { schema: getDpiaHistoryResponseSchema }),
    throwOnError: true,
  });

/** Get Dpia form  */
export const dpiaFormQueryOptions = () => {
  return queryOptions({
    queryKey: dpiaKeys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/form`) as Promise<Form>,
    throwOnError: true,
    staleTime: 1000 * 60,
  });
};

/** Update dpia */
type UpdateDpiaPayload = {
  name?: string;
  summary?: string;
  status?: ExternalDpiaStatus;
} & ({ kind?: "link"; url?: string } | { kind: "file"; fileId: string });

export const updateDpiaMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDpiaPayload }) =>
      api.patch(`${BASE_ENDPOINT}/${id}`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Delete dpia */
export const deleteDpiaMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.delete(`${BASE_ENDPOINT}/${id}`),
    onSuccess: (_, id) => invalidateAll({ ignore: [dpiaKeys.byId(id)] }),
  });

/** Update dpia answers */
export const updateDpiaAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, answers }: { id: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT}/${id}/answers`, answers),
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Update pre-assessment answers */
export const updateDpiaPreAssessmentAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, answers }: { id: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT}/${id}/pre-assessment/answers`, answers),
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Update pre-assessment answers */
export const completeDpiaPreAssessmentMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, skip }: { id: string; skip?: boolean }) =>
      api.post(`${BASE_ENDPOINT}/${id}/pre-assessment/complete?skip=${Boolean(skip)}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** add comment to pre-assessment */
export const addCommentToDpiaPreAssessmentMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ dpiaId, payload }: { dpiaId: string; payload: { comment: string } }) =>
      api.post(`${BASE_ENDPOINT}/${dpiaId}/pre-assessment/comment`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Get Dpia Preassessment form  */
export const dpiaPreAssessmentFormQueryOptions = () => {
  return queryOptions({
    queryKey: dpiaKeys.preAssessmentForm(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/preassessmentform`) as Promise<Form>,
    throwOnError: true,
  });
};

export const myTenantsHierarchyWithCreatePermissionQueryOptions = () =>
  queryOptions({
    queryKey: dpiaKeys.myTenantsHierarchyWithCreatePermission(),
    queryFn: () => api.get(`${TENANT_BASE_ENDPOINT}/me/creatorTenantHierarchy`) as Promise<TenantHierarchy>,
    throwOnError: true,
  });

export const updateDpiaResponsibleMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ dpiaId, userId }: { dpiaId: string; userId: string }) =>
      api.post(`${BASE_ENDPOINT}/${dpiaId}/responsible/${userId}`),
    onSuccess: () => invalidateAll(),
  });

export const UpdateDpiaConnectedTenantErrorCodes = {
  responsibleHasNoAccessToNewTenant: "RESPONSIBLE_DOES_NOT_HAVE_AN_ACCESS_TO_NEW_TENANT",
} as const;

export const updateDpiaConnectedTenantMutationOptions = () =>
  mutationOptions({
    mutationFn: ({
      dpiaId,
      tenantId,
      isConfirm,
    }: {
      dpiaId: string;
      tenantId: string;
      tenantName: string;
      isConfirm?: boolean;
    }) => api.post(`${BASE_ENDPOINT}/${dpiaId}/tenant/${tenantId}${isConfirm ? "/confirm" : ""}`),
    onSuccess: () => invalidateAll(),
  });

export const updateDpiaCollaboratorsMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ dpiaId, userIds }: { dpiaId: string; userIds: Array<string>; loggedInUserLosesAccess?: boolean }) =>
      api.patch(`${BASE_ENDPOINT}/${dpiaId}/collaborators`, { userIds }),
    onSuccess: (_, { dpiaId, loggedInUserLosesAccess }) =>
      invalidateAll({ ignore: loggedInUserLosesAccess ? [dpiaKeys.byId(dpiaId)] : [] }),
  });

export const updateDpiaStatusMutationOptions = () =>
  mutationOptions({
    mutationFn: ({
      dpiaId,
      status,
      approverId,
      comment,
    }: {
      dpiaId: string;
      status: DpiaStatus;
      approverId?: string;
      comment?: string;
    }) => api.patch(`${BASE_ENDPOINT}/${dpiaId}/status`, { status, approverId, comment }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const updateDpiaShareAccessMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ dpiaId, shareWith }: { dpiaId: string; shareWith: DpiaShareWithType }) =>
      api.patch(`${BASE_ENDPOINT}/${dpiaId}/share-access`, { shareWith }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const exportDpiaPdfMutationOptions = () =>
  mutationOptions({
    mutationFn: async ({ dpiaId }: { dpiaId: string }) =>
      api.get(`${BASE_ENDPOINT}/${dpiaId}/export-pdf`) as Promise<Response>,
  });

/** Create Dpia risk assessment */
export const createDpiaRiskAssessmentMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: DpiaRiskAssessmentPayload) =>
      api.post(`${BASE_ENDPOINT_RISK_ASSESSMENT}`, payload) as Promise<DpiaRiskAssessmentsResponse>,
    onSuccess: () => queryClient.invalidateQueries(),
  });

/** Get all Dpia risk assessments  */
export const dpiaRiskAssessmentsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: dpiaKeys.riskAssessment(id),
    queryFn: () => api.get(`${BASE_ENDPOINT_RISK_ASSESSMENT}/${id}/all`) as Promise<DpiaRiskAssessmentsResponse>,
    throwOnError: true,
  });

/** Update dpia risk assessment answers */
export const useUpdateDpiaRiskAssessmentById = () => {
  return useMutation({
    mutationFn: ({ riskId, answers }: { riskId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT_RISK_ASSESSMENT}/${riskId}/answers`, answers) as Promise<
        DpiaRiskAssessmentsResponse["riskAssessments"][0]
      >,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Delete dpia risk assesment */
export const deleteDpiaRiskAssessmentMutationOptions = () => {
  return mutationOptions({
    mutationFn: (riskId: string) => api.delete(`${BASE_ENDPOINT_RISK_ASSESSMENT}/${riskId}`),
    onSuccess: (_, riskId) => invalidateAll({ ignore: [dpiaKeys.riskAssessment(riskId)] }),
  });
};

/** Get dpia risk assessment matrix by dpia id */
export const dpiaRiskAssessmentMatrixByIdQueryOptions = (dpiaId: string) =>
  queryOptions({
    queryKey: dpiaKeys.matrix(dpiaId),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT_RISK_ASSESSMENT}/${dpiaId}/matrix`) as Promise<DpiaRiskAssessmentMatrixResponse>,
    staleTime: 1000 * 60,
  });
